// src/settings/NotificationSettings.jsx
'use client'

import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  updateDoc,
  query,
  limit,
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db, app } from '@/firebase/firebase'
import { useAuth } from '@/context/AuthContext'
import useEntitlements from '@/hooks/useEntitlements'
import { toast } from 'react-hot-toast'
import {
  SMS_CONSENT_TEXT,
  SMS_CONSENT_VERSION,
  CONSENT_SOURCES,
} from '@/constants/consent'
import { Switch, Tab } from '@headlessui/react'
import clsx from 'clsx'
import {
  BellIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
  AdjustmentsHorizontalIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

/* ---------- Lime/pitch design tokens ---------- */
const HEAD_FONT = "'Barlow Condensed', sans-serif"

/* =============================================================================
    Helpers & Components
============================================================================= */

function SectionLabel({ children }) {
  return (
    <div
      className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </div>
  )
}

function PrimaryButton({ children, className = '', disabled, ...props }) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all',
        'bg-[#b5f03d] hover:bg-[#94cc28]',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#b5f03d]',
        className
      )}
      style={{ fontFamily: HEAD_FONT }}
      {...props}
    >
      {children}
    </button>
  )
}

function SectionCard({ children, title, icon: Icon, description, right }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="border-b border-zinc-100 px-6 py-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]">
            {Icon && <Icon className="w-5 h-5" />}
          </div>
          <div>
            <h3
              className="text-base font-bold uppercase tracking-wide text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {title}
            </h3>
            {description && <p className="text-xs text-zinc-500">{description}</p>}
          </div>
        </div>
        {right && <div>{right}</div>}
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

const Toggle = ({
  label,
  description,
  enabled,
  onChange,
  disabled = false,
  className = '',
}) => (
  <Switch.Group as="div" className={clsx('py-3 flex items-center justify-between gap-4', className)}>
    <div className="flex-1 min-w-0">
      <Switch.Label as="div" className="text-sm font-medium text-zinc-900" passive>
        {label}
      </Switch.Label>
      {description && <Switch.Description as="p" className="mt-1 text-xs text-zinc-500">{description}</Switch.Description>}
    </div>
    <Switch
      checked={!!enabled}
      onChange={onChange}
      disabled={disabled}
      className={clsx(
        enabled ? 'bg-[#94cc28]' : 'bg-zinc-200',
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#b5f03d] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
      )}
    >
      <span
        className={clsx(
          enabled ? 'translate-x-5' : 'translate-x-0',
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
        )}
      />
    </Switch>
  </Switch.Group>
)

function HourSelect({ value, onChange, disabled = false }) {
  const hours = Array.from({ length: 24 }, (_, i) => i)
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
      className="h-9 w-full rounded-lg border border-zinc-200 bg-white px-2 text-sm text-zinc-900 shadow-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 disabled:opacity-50"
    >
      {hours.map((h) => (
        <option key={h} value={h}>
          {h === 0 ? '12 AM' : h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`}
        </option>
      ))}
    </select>
  )
}

function Pill({ icon: Icon, children, color = 'lime' }) {
  const colors = {
    zinc: 'bg-zinc-100 text-zinc-700',
    lime: 'bg-[#b5f03d]/15 text-[#3d6010] ring-1 ring-[#b5f03d]/40',
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
  }
  return (
    <span className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', colors[color] || colors.lime)}>
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  )
}

const LoadingSkeleton = () => (
  <div className="mx-auto max-w-2xl pt-2 pb-20 space-y-8 animate-pulse">
    <div className="h-20 w-1/3 bg-zinc-200 rounded-lg" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-6">
        <div className="h-96 bg-zinc-100 rounded-2xl" />
      </div>
      <div className="h-64 bg-zinc-100 rounded-2xl" />
    </div>
  </div>
)

/* =============================================================================
    MAIN COMPONENT
============================================================================= */

export default function NotificationSettings() {
  const navigate = useNavigate()
  const { firebaseUser, userProfile, enablePushForUser } = useAuth()
  const { isPro, loading: entitlementsLoading } = useEntitlements(firebaseUser?.uid)

  // Parents have no Scrimly subscription and don't make weather decisions
  // (those are coach actions). Hide the Founder's Trial banner, the Pro
  // pill, and the Weather Alerts section for parents.
  const isParent = String(userProfile?.role || '').toLowerCase() === 'parent'

  const [settings, setSettings] = useState({
    // Chat
    newChatPush: true, newChatEmail: false,
    // Game Day
    gameDayPush: true, gameDaySms: false,
    // Cancellations
    cancellationSmsOptIn: true,
    // Master
    smsOptIn: false, quietHoursEnabled: true, quietStartHour: 22, quietEndHour: 7,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [userPhoneNumber, setUserPhoneNumber] = useState(null)

  const [pushPermission, setPushPermission] = useState('default')
  const [hasFcmToken, setHasFcmToken] = useState(false)
  const [enablingPush, setEnablingPush] = useState(false)

  const pushReady = pushPermission === 'granted' && hasFcmToken
  const isValidPhone = useMemo(
    () => userPhoneNumber && /^\+\d{10,15}$/.test(userPhoneNumber),
    [userPhoneNumber]
  )

  // --- Push Logic ---
  const refreshPermission = useCallback(() => {
    if (typeof Notification !== 'undefined') setPushPermission(Notification.permission)
    else setPushPermission('unsupported')
  }, [])

  const checkTokenPresence = useCallback(async () => {
    if (!firebaseUser?.uid) return false
    try {
      const q = query(collection(db, `users/${firebaseUser.uid}/fcmTokens`), limit(1))
      const snap = await getDocs(q)
      return !snap.empty
    } catch { return false }
  }, [firebaseUser?.uid])

  const ensurePushEnabled = useCallback(async () => {
    if (!firebaseUser) return false
    refreshPermission()
    const currentPerm = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'

    if (currentPerm === 'unsupported') {
      toast.error('Push unsupported')
      return false
    }
    if (currentPerm === 'denied') {
      toast.error('Push blocked in browser settings')
      return false
    }
    if (currentPerm !== 'granted') {
      try {
        const perm = await Notification.requestPermission()
        setPushPermission(perm)
        if (perm !== 'granted') return false
      } catch { return false }
    }

    setEnablingPush(true)
    try {
      await enablePushForUser(firebaseUser)
      const tokenOk = await checkTokenPresence()
      setHasFcmToken(!!tokenOk)
      refreshPermission()
      if (tokenOk) toast.success('Push enabled')
      return !!tokenOk
    } catch { return false }
    finally { setEnablingPush(false) }
  }, [firebaseUser, enablePushForUser, refreshPermission, checkTokenPresence])

  // --- Load Data ---
  useEffect(() => {
    if (!firebaseUser?.uid) return
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const userSnap = await getDoc(doc(db, `users/${firebaseUser.uid}`))
        if (userSnap.exists()) {
          const u = userSnap.data()
          setUserPhoneNumber(u.phoneNumber || u?.phone?.phoneNumber || null)
        }
        const setSnap = await getDoc(doc(db, `users/${firebaseUser.uid}/notificationSettings/default`))
        if (setSnap.exists()) {
          setSettings(prev => ({ ...prev, ...setSnap.data() }))
        }
      } catch { toast.error('Failed to load settings') }
      finally { setIsLoading(false) }
    }
    fetchData()
  }, [firebaseUser?.uid])

  // --- Push Watcher ---
  useEffect(() => {
    if (!firebaseUser?.uid) return
    refreshPermission()
    const unsub = onSnapshot(
      query(collection(db, `users/${firebaseUser.uid}/fcmTokens`), limit(1)),
      (snap) => setHasFcmToken(!snap.empty)
    )
    const onFocus = () => refreshPermission()
    window.addEventListener('focus', onFocus)
    return () => { unsub(); window.removeEventListener('focus', onFocus) }
  }, [firebaseUser?.uid, refreshPermission])

  // --- Handlers ---
  const updateSetting = async (newSetting) => {
    if (!firebaseUser) return
    setSettings(prev => ({ ...prev, ...newSetting }))
    const ref = doc(db, `users/${firebaseUser.uid}/notificationSettings/default`)
    await setDoc(ref, { ...newSetting, updatedAt: serverTimestamp() }, { merge: true })

    // Master SMS toggle changes are TCPA-relevant — record an audit event.
    // (recordConsent also mirrors compliance.smsConsent on the user doc, so
    // the legacy direct-write below is no longer needed when this branch
    // succeeds. We keep the direct write as a fallback if the CF call fails.)
    if (Object.prototype.hasOwnProperty.call(newSetting, 'smsOptIn')) {
      const isGranting = !!newSetting.smsOptIn
      try {
        const recordConsent = httpsCallable(getFunctions(app, 'us-central1'), 'recordConsent')
        await recordConsent({
          type: 'sms',
          action: isGranting ? 'granted' : 'revoked',
          phoneNumber: userPhoneNumber || null,
          source: isGranting
            ? CONSENT_SOURCES.NOTIFICATIONS_TOGGLE_ON
            : CONSENT_SOURCES.NOTIFICATIONS_TOGGLE_OFF,
          // Grants need the disclosure snapshot for legal defense; revokes
          // don't (the user isn't agreeing to anything new).
          consentTextVersion: isGranting ? SMS_CONSENT_VERSION : null,
          consentText: isGranting ? SMS_CONSENT_TEXT : null,
          identitySnapshot: {
            displayName: userProfile?.displayName || firebaseUser.displayName || null,
            email: firebaseUser.email || null,
          },
        })
      } catch (logErr) {
        console.warn('[NotificationSettings] consent log failed; falling back to direct write', logErr)
        // Fallback so the user-doc compliance flag still tracks the toggle
        // (audit trail will be incomplete but functional state is correct).
        updateDoc(doc(db, `users/${firebaseUser.uid}`), {
          'compliance.smsConsent': isGranting,
          updatedAt: serverTimestamp(),
        }).catch(() => {})
      }
    }
  }

  const handlePushPrefToggle = async (key, value) => {
    if (value === true && !pushReady) {
      const ok = await ensurePushEnabled()
      if (!ok) {
        setSettings(prev => ({ ...prev, [key]: false }))
        return
      }
    }
    await updateSetting({ [key]: value })
  }

  if (isLoading || entitlementsLoading) return <LoadingSkeleton />

  const pushStatusTone = pushPermission === 'denied' ? 'red' : pushReady ? 'lime' : 'amber'
  const pushStatusLabel = pushPermission === 'denied' ? 'Blocked' : pushReady ? 'Active' : 'Setup Required'

  return (
    <div className="mx-auto max-w-2xl pt-2 pb-20 space-y-8">

      {/* Founder's Trial Banner — coach-only, parents have no subscription */}
      {!isPro && !isParent && (
        <div className="mb-8 rounded-2xl p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-r from-[#b5f03d] to-[#94cc28] text-[#0d1f14]">
          <div className="flex items-center gap-3">
            <SparklesIcon className="h-8 w-8" />
            <div>
              <p
                className="font-bold text-lg leading-tight uppercase tracking-wide"
                style={{ fontFamily: HEAD_FONT }}
              >
                Founder's Trial Active
              </p>
              <p className="text-sm opacity-80">
                Enjoy 30 days of automated notifications and SMS alerts for free.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/billing/upgrade')}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide bg-[#0d1f14] text-[#b5f03d] hover:bg-[#1a3622] whitespace-nowrap transition-all"
            style={{ fontFamily: HEAD_FONT }}
          >
            Upgrade Early
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <SectionLabel>Settings</SectionLabel>
        <div className="mt-1 flex items-center gap-3 mb-1">
          <h1
            className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Notifications
          </h1>
          {!isParent && (
            isPro ? (
              <Pill color="lime" icon={SparklesIcon}>Pro</Pill>
            ) : (
              <Pill color="lime" icon={CheckCircleIcon}>Trial Active</Pill>
            )
          )}
        </div>
        <p className="mt-1 text-sm text-zinc-500">
          Control how and when you get notified about chat, games, and weather.
        </p>
      </div>

      {/* Delivery Channels (master config first) */}
      <SectionCard title="Delivery Channels" icon={DevicePhoneMobileIcon}>
        <div className="space-y-6">
          {/* Push Status Master Control */}
          <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200 bg-zinc-50/50">
            <div className="flex items-center gap-2">
              <BellIcon className="h-5 w-5 text-zinc-500" />
              <span className="text-sm font-medium text-zinc-700">Push Status</span>
            </div>
            <Pill color={pushStatusTone}>{pushStatusLabel}</Pill>
          </div>
          {pushPermission !== 'granted' && (
            <PrimaryButton className="w-full" onClick={ensurePushEnabled} disabled={enablingPush}>
              {enablingPush ? 'Enabling…' : 'Enable Push Notifications'}
            </PrimaryButton>
          )}

          <div className="border-t border-zinc-100" />

          {/* Master SMS Opt-In */}
          <div>
            <Toggle
              label="Master SMS"
              description="Required to receive any text alerts."
              enabled={settings.smsOptIn}
              onChange={(v) => updateSetting({ smsOptIn: v })}
              disabled={!isValidPhone}
            />
            {!isValidPhone && (
              <p className="text-xs text-red-600 mt-1 font-medium italic">
                Add a verified phone number to profile to enable SMS.
              </p>
            )}
            {isValidPhone && (
              <p className="text-xs text-zinc-500 mt-1 font-medium">Verified: {userPhoneNumber}</p>
            )}
          </div>

          {/* Quiet Hours Control */}
          <div className="pt-4 border-t border-zinc-100">
            <Toggle
              label="Quiet Hours"
              description="Pause non-urgent SMS alerts."
              enabled={settings.quietHoursEnabled}
              onChange={(v) => updateSetting({ quietHoursEnabled: v })}
            />
            <div className={clsx("mt-3 grid grid-cols-2 gap-3 transition-opacity", !settings.quietHoursEnabled && "opacity-50 pointer-events-none")}>
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500 block mb-1">Start Time</label>
                <HourSelect value={settings.quietStartHour} onChange={(v) => updateSetting({ quietStartHour: v })} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500 block mb-1">End Time</label>
                <HourSelect value={settings.quietEndHour} onChange={(v) => updateSetting({ quietEndHour: v })} />
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Event Preferences" icon={AdjustmentsHorizontalIcon} description="Customize alerts for specific activities.">
            <Tab.Group>
              <Tab.List className="flex gap-1 rounded-xl bg-zinc-100 p-1 overflow-x-auto">
                {['Chat', 'Game Day', 'Urgent'].map((name) => (
                  <Tab
                    key={name}
                    className={({ selected }) =>
                      clsx(
                        'w-full rounded-lg py-2 text-xs font-bold uppercase tracking-wide transition-all focus:outline-none whitespace-nowrap px-4',
                        selected
                          ? 'bg-white text-[#0d1f14] shadow-sm'
                          : 'text-zinc-500 hover:text-zinc-700'
                      )
                    }
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {name}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="mt-6">

                {/* Chat Panel */}
                <Tab.Panel className="space-y-2">
                  <Toggle label="Push" description="New message alerts." enabled={settings.newChatPush} onChange={(v) => handlePushPrefToggle('newChatPush', v)} />
                  <Toggle label="Email Summary" description="Missed message digest." enabled={settings.newChatEmail} onChange={(v) => updateSetting({ newChatEmail: v })} />
                </Tab.Panel>

                {/* Game Day Panel */}
                <Tab.Panel className="space-y-2">
                  <Toggle label="Push Reminder" description="24h before kickoff." enabled={settings.gameDayPush} onChange={(v) => handlePushPrefToggle('gameDayPush', v)} />
                  <Toggle label="SMS Reminder" description="2h before kickoff." enabled={settings.gameDaySms} onChange={(v) => updateSetting({ gameDaySms: v })} disabled={!settings.smsOptIn} />
                </Tab.Panel>

                {/* Urgent Panel */}
                <Tab.Panel className="space-y-2">
                  <div className="bg-red-50 p-3 rounded-lg text-xs text-red-700 mb-4 border border-red-100 font-medium">
                    Cancellations always send Push & Email.
                  </div>
                  <Toggle
                    label="Urgent SMS"
                    description="Bypasses quiet hours for last-minute cancellations."
                    enabled={settings.cancellationSmsOptIn}
                    onChange={(v) => updateSetting({ cancellationSmsOptIn: v })}
                    disabled={!settings.smsOptIn}
                  />
                </Tab.Panel>

              </Tab.Panels>
            </Tab.Group>
          </SectionCard>

      {/* Weather Alerts section removed pending backend wiring.
          Toggles weatherAlertPush/Email/FollowupPush exist as defaults in
          functions/modules/auth.js but are NOT consumed anywhere in the
          weather-alert delivery path — they were decorative. Re-add this
          section once weatherAlerts.js reads notification settings before
          dispatching push/email. Same goes for the iOS Live Activity
          toggle (no native implementation today). */}
    </div>
  )
}
